import * as React from "react";
import {
	type ColumnDef,
	type ColumnFiltersState,
	type VisibilityState,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { Button } from "../../../components/ui/button";
import { DataTableComponents } from "../../../components/tables/table.components";
import Spinner from "../../../components/spinner.components";
import { Link } from "react-router-dom";
import { useGetBrandsAdminHttp } from "../../../hooks/http/admin/brands/get-brand.admin.http";
import useBrandsStore from "../../../store/brands.store";
import { useCallback, useEffect } from "react";
import type { IBrand } from "../../../types/models/brands.models.types";
import type { IPagination } from "../../../types/models/pagination.models.types";
import { useSearchBrandsAdminHttp } from "../../../hooks/http/admin/brands/search-brands.admin.http";
import { debounce } from "../../../utils/utils";
import { useToast } from "../../../hooks/use-toast";

type AppProps = {
	search?: string;
};

export function DataTableBrandComponents({ search }: AppProps): JSX.Element {
	const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
		[],
	);
	const [columnVisibility, setColumnVisibility] =
		React.useState<VisibilityState>({});
	const [rowSelection, setRowSelection] = React.useState({});
	const { data, setData } = useBrandsStore();
	const [, setPageNo] = React.useState(1);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [pagination, setPaginationData] = React.useState<IPagination>();
	const {
		execute: getBrandsHttp,
		fetching: fetchingBrands,
		cancel: cancelGetBrandsHttp,
	} = useGetBrandsAdminHttp();
	const {
		execute: searchBrandsHttp,
		fetching: searchFetching,
		cancel: cancelSearchBrandHttp,
	} = useSearchBrandsAdminHttp();
	const { toast } = useToast();
	const [typing, setTyping] = React.useState(false);

	const debouncedSearch = useCallback(
		debounce(async (searchValue: string) => {
			const searchString = searchValue.trim();
			if (!searchString) {
				return;
			}

			const response = await searchBrandsHttp({
				body: null,
				queryParams: {
					searchString: searchString,
				},
			});
			if (!response.success) {
				if (response.ignoreError) {
					return;
				}
				return toast({
					content: response.error.message,
					toastType: "error",
				});
			}

			setData(response.data.brands);
			setTyping(false);
		}, 500),
		[],
	);

	async function getBrands(existingData: IBrand[], page: number) {
		cancelGetBrandsHttp();
		const response = await getBrandsHttp({
			body: null,
			queryParams: { page },
		});

		if (!response.success) {
			if (response.ignoreError) {
				return;
			}
			return toast({
				content: response.error.message,
				toastType: "error",
			});
		}

		setData([...existingData, ...response.data.brands]);
		setPaginationData({
			page: 0,
			totalPages: 0,
			totalPosts: 0,
		});
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		setData([]);
		if (search?.length) {
			cancelSearchBrandHttp();
			debouncedSearch(search);
			setTyping(true);
		} else {
			setPageNo(1);
			getBrands([], 1);
		}
	}, [search]);


	const columns: ColumnDef<IBrand>[] = [
		{
			accessorKey: "name",
			header: "Brand name",
			cell: ({ row }) => {
				const { name } = row.original;

				return (
					<div className="flex items-center gap-3 font-semibold tracking-wider">
						<img
							width={30}
							height={30}
							className="border-2 rounded-full"
							src="/public/static/profile.png"
							alt=""
						/>
						{name}
					</div>
				);
			},
		},

		{
			accessorKey: "id",
			header: "",
			cell: ({ row }) => (
				<div className="flex justify-end items-center gap-4">
					<Link to={`/admin/brands/${row.getValue("id")}/create-campaign`}>
						<Button className="bg-tertiarybuttonbg px-5 py-2.5 border border-border rounded-md font-semibold tracking-wider">
							Create campaign
						</Button>
					</Link>
					{/* <Button
						onClick={() => {
							handleViewDetails(row.getValue("id"));
						}}
						className="bg-tertiarybuttonbg p-2.5 border border-border rounded-md font-semibold tracking-wider"
					>
						View Details
					</Button> */}
				</div>
			),
		},
	];

	const table = useReactTable({
		data,
		columns,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		onRowSelectionChange: setRowSelection,
		state: {
			columnFilters,
			columnVisibility,
			rowSelection,
		},
		manualPagination: true,
	});

	if ((data.length <= 0 && fetchingBrands) || searchFetching || typing) {
		return (
			<div className="flex justify-center items-center pt-20 w-full">
				<Spinner />
			</div>
		);
	}
	if (!pagination) {
		return <></>;
	}

	return (
		<div className="">
			<DataTableComponents
				table={table as ReturnType<typeof useReactTable>}
				columns={columns as ColumnDef<IBrand>[]}
				setPageNo={() => {
					setPageNo((prev) => {
						const nextPage = prev + 1;
						getBrands(data, nextPage);
						return nextPage;
					});
				}}
				loading={fetchingBrands || searchFetching || false}
				tableType="brand"
				paginationData={pagination}
			/>
		</div>
	);
}
